import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { ConvexBetterAuthProvider } from "@convex-dev/better-auth/react";
import { Direction } from "radix-ui";
import { useTranslation } from "react-i18next";

import "./index.css";
import { routeTree } from "./routeTree.gen";
import { authClient } from "@/lib/auth-client";
import { convex } from "@/lib/convex";
import { initSentry, Sentry } from "@/lib/sentry";
import { i18nReady } from "@/i18n";
import { UpdateNotice } from "@/components/update-notice";
import { api } from "../convex/_generated/api";

initSentry();

if (import.meta.env.DEV) {
  (window as unknown as { convex: typeof convex; api: typeof api }).convex =
    convex;
  (window as unknown as { convex: typeof convex; api: typeof api }).api = api;
}

const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  scrollRestoration: true,
  context: { authClient },
  defaultOnCatch: (error) => {
    Sentry.captureException(error);
  },
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// Wait for the active language's catalogs so non-English visitors don't see
// an English flash. If loading fails, render anyway with the English fallback.
void i18nReady
  .catch((error: unknown) => Sentry.captureException(error))
  .finally(() => {
    createRoot(document.getElementById("root")!).render(
      <StrictMode>
        <DirectionRoot>
          <Sentry.ErrorBoundary fallback={<RootCrashFallback />}>
            <ConvexBetterAuthProvider client={convex} authClient={authClient}>
              <RouterProvider router={router} />
            </ConvexBetterAuthProvider>
          </Sentry.ErrorBoundary>
          <UpdateNotice />
        </DirectionRoot>
      </StrictMode>,
    );
  });

/** Tells Radix primitives (menus, sliders, …) which way text flows. */
function DirectionRoot({ children }: { children: React.ReactNode }) {
  const { i18n } = useTranslation();
  return (
    <Direction.Provider dir={i18n.dir()}>{children}</Direction.Provider>
  );
}

function RootCrashFallback() {
  const { t } = useTranslation();
  return (
    <main className="mx-auto flex min-h-svh max-w-md flex-col items-center justify-center gap-4 px-6 text-center">
      <p className="font-display italic text-xl text-ink-2">
        {t("crash.title")}
      </p>
      <p className="text-sm text-ink-3">{t("crash.body")}</p>
    </main>
  );
}
